import React, { useState, useEffect, useRef } from 'react';
import { Phone, Smartphone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const CallButton = ({ number, description, className = '', size = 'sm', variant = 'icon', children }) => {
  const [devices, setDevices] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [calling, setCalling] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    if (showMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const loadDevices = async () => {
    if (loaded) return devices;
    try {
      const res = await api.get('/centralino/my-devices');
      const d = res.data.devices || [];
      setDevices(d);
      setLoaded(true);
      return d;
    } catch {
      return [];
    }
  };

  const makeCall = async (deviceExtension) => {
    setShowMenu(false);
    setCalling(true);
    try {
      const payload = { number, description: description || '' };
      if (deviceExtension) payload.device_extension = deviceExtension;
      await api.post('/centralino/call', payload);
      toast.success('Chiamata avviata! Il telefono sta squillando...');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore chiamata');
    } finally {
      setCalling(false);
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!number) { toast.error('Nessun numero'); return; }
    const devs = await loadDevices();
    if (devs.length <= 1) {
      makeCall(devs[0]?.extension || null);
    } else {
      setShowMenu(true);
    }
  };

  if (variant === 'icon') {
    return (
      <div className="relative" ref={menuRef}>
        <button onClick={handleClick} disabled={calling} className={`p-1.5 rounded-lg transition-colors text-slate-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 ${className}`} title="Chiama" data-testid="call-btn">
          {calling ? <Loader2 size={13} className="animate-spin" /> : <Phone size={13} />}
        </button>
        {showMenu && (
          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 min-w-[180px]" data-testid="device-menu">
            <p className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Chiama da:</p>
            {devices.map(d => {
              const isApp = /app|mobile|cell| 2/i.test(d.description);
              return (
                <button key={d.extension} onClick={() => makeCall(d.extension)} className="flex items-center gap-2 w-full px-3 py-2 text-xs text-slate-700 hover:bg-blue-50 transition-colors" data-testid={`device-${d.extension}`}>
                  {isApp ? <Smartphone size={14} className="text-blue-500" /> : <Phone size={14} className="text-slate-400" />}
                  <span>{d.description}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button onClick={handleClick} disabled={calling} className={className} data-testid="call-btn">
        {calling ? <Loader2 size={14} className="animate-spin mr-1.5" /> : (children || <><Phone size={14} className="mr-1.5" /> Chiama</>)}
      </button>
      {showMenu && (
        <div className="absolute left-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 min-w-[180px]" data-testid="device-menu">
          <p className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Chiama da:</p>
          {devices.map(d => {
            const isApp = /app|mobile|cell| 2/i.test(d.description);
            return (
              <button key={d.extension} onClick={() => makeCall(d.extension)} className="flex items-center gap-2 w-full px-3 py-2 text-xs text-slate-700 hover:bg-blue-50 transition-colors" data-testid={`device-${d.extension}`}>
                {isApp ? <Smartphone size={14} className="text-blue-500" /> : <Phone size={14} className="text-slate-400" />}
                <span>{d.description}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CallButton;
